// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  GridRow,
  GridRowProps,
} from '@mui/x-data-grid-premium';
import { daDK } from '@mui/x-data-grid/locales';

// shared components
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar';

// Define and export types for the component
export interface Service {
  Id: string | number;
  Name: string;
}

export interface Account {
  AccountId: string | number;
  Name: string;
  Address: string;
  Services: Service[];
}

export interface ServiceManagerDatagridProps {
  accounts?: Account[];
  isLoading?: boolean;
}

export interface DataGridRow extends Account {
  id: string | number;
}

function LinkRow(props: GridRowProps) {
  const rowAccountId = (props.row as DataGridRow)?.AccountId;

  return (
    <Link
      to={`/$accountId/service-manager/${rowAccountId}` as never}
      style={{ display: 'contents', textDecoration: 'none', color: 'inherit' }}
    >
      <GridRow {...props} />
    </Link>
  );
}

const ServiceManagerDatagrid: React.FC<ServiceManagerDatagridProps> = ({
  accounts = [],
  isLoading = false,
}) => {
  const { t } = useTranslation('service-manager');

  const rows: DataGridRow[] = accounts.map((account) => ({
    ...account,
    id: account.AccountId,
  }));

  const columns: GridColDef[] = [
    {
      field: 'AccountId',
      headerName: t('dataGrid.columns.accountId'),
      flex: 1,
    },
    { field: 'Name', headerName: t('dataGrid.columns.accountName'), flex: 2 },
    { field: 'Address', headerName: t('dataGrid.columns.address'), flex: 2 },
    {
      field: 'Services',
      headerName: t('dataGrid.columns.services'),
      flex: 3,
      renderCell: (params: GridRenderCellParams) => {
        const services = params?.value as Service[];
        if (!Array.isArray(services)) return '';
        return services
          .map((s) => s?.Name)
          .filter(Boolean)
          .join(', ');
      },
    },
  ];

  return (
    <DataGridPremium
      loading={isLoading}
      rows={rows}
      columns={columns}
      localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
      pagination
      initialState={{
        pagination: { paginationModel: { page: 0, pageSize: 25 } },
      }}
      pageSizeOptions={[25, 50, 100]}
      showToolbar
      slots={{ toolbar: DataGridToolbar, row: LinkRow }}
      slotProps={{
        loadingOverlay: {
          variant: 'linear-progress',
          noRowsVariant: 'skeleton',
        },
      }}
    />
  );
};

export default ServiceManagerDatagrid;
